import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {makeMapStateToProps} from '../../../helpers/reselectHelpers';
import {ActivityVisualTypesEnum} from '../../../models/api/activityModel';
import {AppState} from '../../../store/appState';
import {getActivities, getComments, getConversations} from '../../../store/data/dataSelectors';
import {FloatingActivityDropdown} from './floatingActivityDropdown';

/*
 * Props.
 */

interface FloatingActivityDropdownContainerProps {
  activityId: number;
}

/*
 * Selectors.
 */

function getActivityId(_state: AppState, props: FloatingActivityDropdownContainerProps) {
  return props.activityId;
}

function makeGetActivity() {
  return createSelector([getActivityId, getActivities], (activityId, activities) => activities[activityId]);
}

function makeGetComment(getActivity: ReturnType<typeof makeGetActivity>) {
  return createSelector([getActivity, getComments], (activity, comments) => {
    if (!activity || activity.visualType !== ActivityVisualTypesEnum.COMMENT) {
      return undefined;
    }

    return comments[activity.commentUid];
  });
}

function makeGetConversationId(getComment: ReturnType<typeof makeGetComment>) {
  return createSelector(getComment, (comment) => comment?.conversationId);
}

function makeGetNamespace(getConversationId: ReturnType<typeof makeGetConversationId>) {
  return createSelector([getConversationId, getConversations], (conversationId, conversations) =>
    conversationId ? conversations[conversationId]?.namespace : undefined,
  );
}

const mapStateToProps = makeMapStateToProps<FloatingActivityDropdownContainerProps>()(() => {
  const getActivity = makeGetActivity();
  const getComment = makeGetComment(getActivity);
  const getConversationId = makeGetConversationId(getComment);
  const getNamespace = makeGetNamespace(getConversationId);

  return {
    comment: getComment,
    conversationId: getConversationId,
    namespace: getNamespace,
  };
});

export const FloatingActivityDropdownContainer = connect(mapStateToProps)(FloatingActivityDropdown);
